import { motion } from 'framer-motion';
import {
    ArrowRight,
    CheckCircle,
    Code,
    Database,
    Key,
    Link as LinkIcon,
    Lock,
    Network,
    Play,
    QrCode,
    Shield,
    Smartphone,
    Users,
    Zap
} from 'lucide-react';
import { useState } from 'react';

const Documint = () => {
    const [selectedMethod, setSelectedMethod] = useState('blockchain');
    const [activeDemo, setActiveDemo] = useState(0);

    const didMethods = [
        {
            id: 'blockchain',
            name: 'Blockchain DIDs',
            icon: Database,
            description: 'Immutable, decentralized identity anchored on blockchain networks',
            benefits: ['Permanent record', 'Global consensus', 'No single authority', 'Cryptographic security'],
            examples: ['did:ethr:', 'did:ion:', 'did:polygon:']
        },
        {
            id: 'jwk',
            name: 'JSON Web Key (JWK)',
            icon: Key,
            description: 'Self-contained DIDs using cryptographic keys without external registries',
            benefits: ['No external dependencies', 'Immediate creation', 'Lightweight', 'Standards compliant'],
            examples: ['did:jwk:', 'did:key:']
        },
        {
            id: 'web',
            name: 'Web DIDs',
            icon: Network,
            description: 'DIDs hosted on existing web infrastructure using HTTPS',
            benefits: ['Easy deployment', 'Web integration', 'Familiar infrastructure', 'Cost effective'],
            examples: ['did:web:example.com', 'did:web:identity.documint.id']
        }
    ];

    const demoSteps = [
        {
            title: 'Customer Onboarding',
            description: 'Streamlined KYC process using verifiable credentials',
            icon: Users,
            metrics: '80% faster onboarding'
        },
        {
            title: 'Credential Verification',
            description: 'Instant verification of educational and professional credentials',
            icon: CheckCircle,
            metrics: '99.9% accuracy rate'
        },
        {
            title: 'Ecosystem Network',
            description: 'Interconnected network of trusted issuers and verifiers',
            icon: Network,
            metrics: '500+ verified partners'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-surface-100 via-surface-200 to-surface-100">

            {/* Why Documint Section */}
            <section id="why-documintid" className="py-20 bg-surface-200/40">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                            Why Documint?
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            As our identity becomes more digital, the demand for Documint technology is growing. 
                            Here&apos;s why decentralized identity is the future.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Shield,
                                title: 'Enhanced Security',
                                description: 'Cryptographic proof eliminates identity fraud and provides tamper-evident credentials that can be verified instantly.'
                            },
                            {
                                icon: Lock,
                                title: 'Privacy Control',
                                description: 'Users control what information they share, when they share it, and with whom - enabling selective disclosure.'
                            },
                            {
                                icon: Zap,
                                title: 'Instant Verification',
                                description: 'Real-time credential verification without contacting the original issuer, reducing time and costs.'
                            },
                            {
                                icon: Network,
                                title: 'Interoperability',
                                description: 'Standards-based approach ensures credentials work across different platforms and organizations globally.'
                            },
                            {
                                icon: Database,
                                title: 'No Single Point of Failure',
                                description: 'Decentralized architecture means no central authority can revoke or control your digital identity.'
                            },
                            {
                                icon: QrCode,
                                title: 'Mobile-First',
                                description: 'Designed for mobile devices with QR code sharing, offline verification, and intuitive user experiences.'
                            }
                        ].map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="glass-card p-6 text-center"
                            >
                                <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-8 h-8 text-neon-300" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-3">
                                    {benefit.title}
                                </h3>
                                <p className="text-gray-300">
                                    {benefit.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How Documint Works Section */}
            <section id="how-documintid-works" className="py-20">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                            How Documint Works
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Documint allows companies to generate tamper-proof digital credentials containing 
                            user information or identifiers using multiple DID methods for maximum flexibility.
                        </p>
                    </motion.div>

                    {/* DID Methods */}
                    <div className="mb-16">
                        <h3 className="text-2xl font-bold text-white text-center mb-8">
                            Multiple DID Creation Methods
                        </h3>
                        
                        {/* Method Selector */}
                        <div className="flex justify-center mb-8">
                            <div className="glass-card p-2 rounded-lg">
                                {didMethods.map((method) => (
                                    <button
                                        key={method.id}
                                        onClick={() => setSelectedMethod(method.id)}
                                        className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                                            selectedMethod === method.id
                                                ? 'bg-primary-600 text-white'
                                                : 'text-gray-300 hover:text-white'
                                        }`}
                                    >
                                        {method.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Selected Method Details */}
                        <motion.div
                            key={selectedMethod}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="max-w-4xl mx-auto"
                        >
                            {didMethods.filter(method => method.id === selectedMethod).map((method) => (
                                <div key={method.id} className="glass-card p-8">
                                    <div className="flex items-center mb-6">
                                        <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mr-4">
                                            <method.icon className="w-8 h-8 text-neon-300" />
                                        </div>
                                        <div>
                                            <h4 className="text-2xl font-bold text-white">{method.name}</h4>
                                            <p className="text-gray-300">{method.description}</p>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-8">
                                        <div>
                                            <h5 className="text-lg font-semibold text-white mb-4">Key Benefits</h5>
                                            <ul className="space-y-2">
                                                {method.benefits.map((benefit, index) => (
                                                    <li key={index} className="flex items-center text-gray-300">
                                                        <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                                                        {benefit}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <h5 className="text-lg font-semibold text-white mb-4">Example DIDs</h5>
                                            <div className="space-y-2">
                                                {method.examples.map((example, index) => (
                                                    <div key={index} className="bg-surface-100/50 rounded-lg p-3">
                                                        <code className="text-neon-300 text-sm">{example}</code>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    {/* Process Steps */}
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                step: '01',
                                title: 'Create DID',
                                description: 'Generate a unique decentralized identifier using your preferred method',
                                icon: Key
                            },
                            {
                                step: '02',
                                title: 'Issue Credentials',
                                description: 'Create tamper-proof verifiable credentials linked to the DID',
                                icon: Shield
                            },
                            {
                                step: '03',
                                title: 'Verify Instantly',
                                description: 'Enable instant verification without contacting the original issuer',
                                icon: Zap
                            }
                        ].map((step, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.2 }}
                                viewport={{ once: true }}
                                className="glass-card p-6 text-center"
                            >
                                <div className="text-4xl font-bold text-primary-400 mb-4">{step.step}</div>
                                <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <step.icon className="w-8 h-8 text-neon-300" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                                <p className="text-gray-300">{step.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Demos Section */}
            <section id="demos" className="py-20 bg-surface-200/40">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                            Live Demos
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            See how Documint accelerates customer onboarding and enables the creation 
                            of a new ecosystem of verifiers through real-world demonstrations.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="space-y-6">
                                {demoSteps.map((demo, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className={`glass-card p-6 cursor-pointer transition-all duration-300 ${
                                            activeDemo === index ? 'border-primary-400 shadow-lg' : ''
                                        }`}
                                        onClick={() => setActiveDemo(index)}
                                    >
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-primary-600/20 rounded-full flex items-center justify-center mr-4">
                                                <demo.icon className="w-6 h-6 text-neon-300" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-white">{demo.title}</h3>
                                                <p className="text-gray-300 text-sm">{demo.description}</p>
                                                <div className="text-primary-400 font-medium text-sm mt-1">
                                                    {demo.metrics}
                                                </div>
                                            </div>
                                            <Play className="w-6 h-6 text-primary-400" />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        <div className="glass-card p-8">
                            <motion.div
                                key={activeDemo}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="aspect-video bg-gradient-to-br from-primary-600/20 to-neon-600/20 rounded-lg flex items-center justify-center mb-6">
                                    <div className="text-center">
                                        <Play className="w-16 h-16 text-white mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-white mb-2">
                                            {demoSteps[activeDemo].title} Demo
                                        </h3>
                                        <p className="text-gray-300">
                                            Interactive demonstration coming soon
                                        </p>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <button className="btn-primary inline-flex items-center">
                                        <Play className="w-5 h-5 mr-2" />
                                        Watch Full Demo
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Technical Specifications */}
            <section className="py-20">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                            Technical Specifications
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Built on open standards and cutting-edge technology for maximum interoperability and security.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                title: 'Standards Compliance',
                                items: ['W3C DID Core 1.0', 'W3C Verifiable Credentials', 'OIDC4VCI', 'OIDC4VP', 'JWT/JWS/JWE']
                            },
                            {
                                title: 'Supported Blockchains',
                                items: ['Ethereum', 'Polygon', 'Hyperledger Indy', 'ION (Bitcoin)', 'Cosmos']
                            },
                            {
                                title: 'Cryptographic Methods',
                                items: ['Ed25519', 'ECDSA (secp256k1)', 'RSA-2048/4096', 'BLS signatures', 'Zero-Knowledge Proofs']
                            },
                            {
                                title: 'Integration Options',
                                items: ['REST APIs', 'GraphQL', 'WebSockets', 'Mobile SDKs', 'Browser Extensions']
                            },
                            {
                                title: 'Security Features',
                                items: ['Hardware Security Modules', 'Key Recovery', 'Credential Revocation', 'Audit Trails', 'Rate Limiting']
                            },
                            {
                                title: 'Enterprise Features',
                                items: ['SSO Integration', 'RBAC', 'Multi-tenancy', 'Custom Schemas', 'Bulk Operations']
                            }
                        ].map((spec, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="glass-card p-6"
                            >
                                <h3 className="text-lg font-semibold text-white mb-4">{spec.title}</h3>
                                <ul className="space-y-2">
                                    {spec.items.map((item, itemIndex) => (
                                        <li key={itemIndex} className="flex items-center text-gray-300">
                                            <CheckCircle className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                            Ready to Build with Documint?
                        </h2>
                        <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                            Start creating decentralized identities today with our comprehensive platform and developer tools.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.a
                                href="https://app.passhub.documint.id/signup"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-white inline-flex items-center"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span>Get Started Free</span>
                                <ArrowRight size={20} className="ml-2" />
                            </motion.a>
                            <motion.a
                                href="/docs"
                                className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-primary-600 transition-colors duration-200 inline-flex items-center"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Code size={20} className="mr-2" />
                                View Documentation
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Documint;